// ABOUTME: The agent's voice playback clock — how much of the utterance currently queued in the
// ABOUTME: Web Audio graph has actually been heard, so the CC can be revealed at speaking pace.

// WHY THIS EXISTS. The transcript and the audio arrive on the same socket but at completely
// different rates: Gemini emits `output_transcription` chunks far faster than the voice they
// describe can be played back, and `App.js` schedules every PCM chunk into an AudioContext queue.
// The caption therefore reached the last sentence of a long answer while the agent was still on the
// first — the reply looked finished before it was spoken.
//
// The fix is not a typewriter effect at a guessed characters-per-second: any fixed rate drifts, and
// drift at the END of a turn is the visible kind. Instead the reveal is driven by the audio's own
// clock, which is the ground truth for "how much has actually been said". The error self-corrects,
// because when the queue drains the progress is 1 and the whole line is on screen by definition.
//
// This module holds no React state on purpose. `App.js` owns the AudioContext and would otherwise
// have to thread a ref down through ReelFooter -> CaptionBar -> the caption leaf; the transcript
// itself already travels as a module-level external store (`transcriptStore`), so its pacing signal
// travels the same way.

let ctx = null;         // the AudioContext the agent's voice is scheduled into
let endsAt = 0;         // ctx.currentTime at which the queued speech finishes
let turnSeconds = 0;    // total audio scheduled for the utterance in progress

/**
 * Record one scheduled chunk of agent speech.
 *
 * @param {AudioContext} audioContext the context the chunk was scheduled into
 * @param {number} startTime          `source.start()` time, in that context's clock
 * @param {number} duration           the chunk's duration in seconds
 */
export function scheduleSpeech(audioContext, startTime, duration) {
  if (!audioContext || !(duration > 0)) return;
  // A context swap (a re-created graph after a reconnect) invalidates the running total.
  if (audioContext !== ctx) {
    ctx = audioContext;
    turnSeconds = 0;
  }
  endsAt = startTime + duration;
  turnSeconds += duration;
}

/**
 * The utterance is over (it finished, the guest barged in, or the backend sent `stop_audio`).
 *
 * Deliberately NOT inferred from a gap in the queue: a network stutter mid-sentence drains the
 * buffer too, and treating that as a new utterance would make the reveal stall for the rest of a
 * perfectly ordinary answer. The turn boundary is a thing `App.js` already knows precisely.
 */
export function resetSpeechClock() {
  endsAt = 0;
  turnSeconds = 0;
}

/**
 * Fraction (0..1) of the current utterance's scheduled audio that has already been heard, or
 * `null` when there is no audio clock to go by — no voice this turn, playback never started, or a
 * test environment. `null` means "reveal everything", i.e. exactly the behaviour before pacing.
 */
export function speechProgress() {
  if (!ctx || turnSeconds <= 0) return null;
  const remaining = endsAt - ctx.currentTime;
  if (!(remaining > 0)) return 1;
  return Math.max(0, Math.min(1, 1 - remaining / turnSeconds));
}

/** Test-only: drop the context reference as well as the timings. */
export function __resetSpeechClockForTests() {
  ctx = null;
  resetSpeechClock();
}
